"use client";

import { buildDigitalTwinDataUrl, getDigitalTwinDataHeaders } from "../../../api/digitalTwin/fieldDataClient";

import { useJsApiLoader } from "@react-google-maps/api";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchDigitalTwinStart } from "../../../store/features/digitalTwin/digitalTwinSlice";
import { fetchPlantsStart } from "../../../store/features/digitalTwin/plantsSlice";
import { fetchPlantsMeasurementsStart } from "../../../store/features/digitalTwin/plantsMeasurementsSlice";
import { fetchRastersStart } from "../../../store/features/digitalTwin/rastersSlice";
import { fetchFieldImagesStart } from "../../../store/features/digitalTwin/fieldImagesSlice";

import OpenStreetMap from "./openStreetMap.component";
import maplibregl from "maplibre-gl";
import {
  createPmtilesObjectUrlFromBase64,
  createPublicRasterUrl,
  getRasterBase64Payload,
  handleDownload,
  isGeoTiffRaster,
  isPmtilesRaster,
} from "../../../utils/digitalTwinUtils";

const libraries = ["places"];

const getApiList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const canReadPublicRasterUrl = async (url) => {
  try {
    console.info("Tentativo download raster da S3 pubblico:", url);
    const response = await fetch(url, {
      headers: {
        Range: "bytes=0-0",
      },
    });

    console.info("Risposta test raster S3:", {
      url,
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
      contentType: response.headers.get("content-type"),
      acceptRanges: response.headers.get("accept-ranges"),
      contentRange: response.headers.get("content-range"),
      accessControlAllowOrigin: response.headers.get("access-control-allow-origin"),
    });

    if (!response.ok) {
      console.error("Download raster S3 non valido:", {
        url,
        status: response.status,
        statusText: response.statusText,
      });
    }

    return response.ok;
  } catch (error) {
    console.error("Errore download raster S3 dal browser. Probabile CORS sul bucket pubblico.", {
      url,
      name: error?.name,
      message: error?.message,
      stack: error?.stack,
      error,
    });
    return false;
  }
};

/**
 * Questo componente renderizza una mappa che contiene i terreni monitorati.
 * Per ciascun terreno è previsto un layer 3D gestito con ThreeJS [Vedi componente ThreeJSLayer].
 * Il componente DigitalTwinMap utilizza una Api Key di Google Maps, e la libreria "places".
 * Quando viene inizializzata la mappa vengono mappati i vari poligoni da mostrare, e la mappa viene centrata in modo che siano tutti visibili.
 *  Il file GLB mock usato per i test sul campo di carciogi si trova nella cartella /public.
 * @returns una Box con ThreeJSLayer (la mappa inizializzata e ThreeJS), LayerCard e LegendCard
 */
export default function DigitalTwinMap() {
  /** We need ad api Key to use Google Maps. */
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyCLIgZfYpxZbB-1pVO5yfMNvGBaUMNKhG4",
    libraries,
  });
  const mapRef = useRef(null);
  const [mapInstance, setMapInstance] = useState(null);
  const selectedField = useSelector((state) => state.selectedDigitalTwin.field);
  const selectedFarm = useSelector((state) => state.selectedDigitalTwin.farm);
  const fieldsData = getApiList(useSelector((state) => state.dTFields?.data));
  const farmsData = getApiList(useSelector((state) => state.farms?.farms?.data));
  const rastersData = useSelector((state) => state.rasters?.data?.data);
  const rastersFieldId = useSelector((state) => state.rasters?.fieldId);
  const rastersLoading = useSelector((state) => state.rasters?.isLoading);
  const latestSurvey = useSelector((state) => state.rasters?.data?.latestSurvey);

  const dispatch = useDispatch();

  const [polygons, setPolygons] = useState([]);
  const [farmPolygons, setFarmPolygons] = useState([]);
  const [polylines, setPolylines] = useState([]);
  const [loading, setLoading] = useState(false);
  const [layers, setLayers] = useState({
    ortofoto: false,
    ndvi: false,
  });

  const [downloadedRaster, setDownloadedRaster] = useState([]);
  const [rasterStatus, setRasterStatus] = useState("idle");

  const plantMarkersRef = useRef([]);

  const digitalTwinData = useSelector((state) => state.digitalTwin?.data?.data);
  const plantsData = useSelector((state) => state.plants?.data?.data);
  const plantsMeasurementsData = useSelector(
    (state) => state.plantsMeasurements?.data?.data,
  );
  const fieldImagesData = useSelector((state) => state.fieldImages?.data);
  const fieldImagesLoading = useSelector((state) => state.fieldImages?.isLoading);
  const selectedFieldImage = useSelector((state) => state.fieldImages?.selectedImage);
  const selectedFieldImageId = useSelector((state) => state.fieldImages?.selectedImageId);
  const fieldImageDetailLoading = useSelector((state) => state.fieldImages?.isDetailLoading);
//url dedicati al test
  const [url, setURL] = useState(
    "/scene/carc_sur_20250404_10x10_local_coord.glb",
  );
  const urlcarc = "/scene/carc_sur_20250404_10x10_local_coord.glb";

  //generare uno status casuale
  function getRandomStatus() {
    const statuses = ["ok", "fine", "bad"];
    return statuses[Math.floor(Math.random() * statuses.length)];
  }

const selectedFieldId = selectedField?.id;

useEffect(() => {
  // Se non c'è un ID valido (all'avvio), usciamo subito
  if (!selectedFieldId) return;

  // Lanciamo i fetch per il nuovo terreno
  dispatch(fetchDigitalTwinStart({ field_id: selectedFieldId }));
  dispatch(fetchPlantsStart({ field_id: selectedFieldId }));
  dispatch(fetchPlantsMeasurementsStart({ field_id: selectedFieldId }));
  dispatch(fetchRastersStart({ field_id: selectedFieldId }));
  dispatch(fetchFieldImagesStart({ field_id: selectedFieldId }));

}, [selectedFieldId, dispatch]); // Includi le dipendenze corrette senza silenziarle
  
  useEffect(() => {
    if (!mapInstance || (!polygons.length && !farmPolygons.length)) return;

    const bounds = new maplibregl.LngLatBounds();

    const selectedArea = selectedField || selectedFarm;

    // 👉 CASE 1: selected field/farm
    if (selectedArea) {
      const coords = selectedArea?.geom?.coordinates?.[0] || [];

      coords.forEach(([lng, lat]) => {
        bounds.extend([lng, lat]);
      });

      if (!bounds.isEmpty()) {
        mapInstance.fitBounds(bounds, {
          padding: 100,
          duration: 800,
        });
      }

      return;
    }

    // 👉 CASE 2: ALL fields/farms
    [...polygons, ...farmPolygons].forEach((polygon) => {
      polygon.forEach(([lng, lat]) => {
        bounds.extend([lng, lat]);
      });
    });

    if (!bounds.isEmpty()) {
      mapInstance.fitBounds(bounds, {
        padding: 100,
        duration: 800,
      });
    }
  }, [farmPolygons, selectedFarm, selectedField, polygons, mapInstance]);
//gestione di poligoni e polilinee
  useEffect(() => {
    if (!fieldsData || !Array.isArray(fieldsData)) {
      setPolygons([]);
      setPolylines([]);
      return;
    }
  
    var polys = fieldsData.map((f) =>
      f?.geom?.coordinates?.[0] && Array.isArray(f.geom.coordinates[0])
        ? f.geom.coordinates[0].map(([lat, lng]) => [lat, lng]) // Map to [lat, lng]
        : [],
    );

    setPolygons(polys);
    setPolylines(polys); // or another set if you have separate lines
  }, [fieldsData]);

  useEffect(() => {
    if (!farmsData || !Array.isArray(farmsData)) {
      setFarmPolygons([]);
      return;
    }

    const polys = farmsData.map((farm) =>
      farm?.geom?.coordinates?.[0] && Array.isArray(farm.geom.coordinates[0])
        ? farm.geom.coordinates[0].map(([lat, lng]) => [lat, lng])
        : [],
    );

    setFarmPolygons(polys);
  }, [farmsData]);
  // 2. Estraiamo il raster da mostrare sulla mappa.
  const getRasterPriority = (raster) => {
    const rasterType = String(raster?.raster_type || "").toLowerCase();
    const methodName = String(raster?.method_name || "").toLowerCase();
    const rasterFile = String(raster?.raster_file || "").toLowerCase();

    if (isGeoTiffRaster(raster)) {
      if (rasterType.includes("cog") && methodName.includes("rgb")) return 100;
      if (rasterType.includes("cog")) return 90;
      if (methodName.includes("orthomosaic")) return 80;
      if (rasterFile.includes("orthomosaic")) return 70;
      return 60;
    }

    if (isPmtilesRaster(raster)) {
      if (methodName.includes("rgb")) return 50;
      return 40;
    }

    return 0;
  };

  const isNdviRaster = (raster) => {
    const rasterType = String(raster?.raster_type || "").trim().toLowerCase();
    const methodName = String(raster?.method_name || "").trim().toLowerCase();

    return rasterType === "geotiff" && methodName === "ndvi map";
  };

  const getRasterTimestamp = useCallback((raster) => {
    const timestamp = new Date(raster?.survey_timestamp || 0).getTime();
    return Number.isNaN(timestamp) ? 0 : timestamp;
  }, []);

  const sortByLatestRaster = useCallback((a, b) => {
    const timestampDiff = getRasterTimestamp(b) - getRasterTimestamp(a);
    if (timestampDiff !== 0) return timestampDiff;

    const surveyDiff = (b?.survey_id || 0) - (a?.survey_id || 0);
    if (surveyDiff !== 0) return surveyDiff;

    return (b?.id || 0) - (a?.id || 0);
  }, [getRasterTimestamp]);

  // 

  // const selectedMapRaster = rastersData
  //  ?.filter((raster) => isGeoTiffRaster(raster) || isPmtilesRaster(raster))
  //   .sort((a, b) => {
  //     const priorityDiff = getRasterPriority(b) - getRasterPriority(a);
  //     if (priorityDiff !== 0) return priorityDiff;
  //     return (b?.id || 0) - (a?.id || 0);
  //   })[0];

  const effectiveRastersData = useMemo(
    () => (String(rastersFieldId) === String(selectedFieldId) ? rastersData : []),
    [rastersData, rastersFieldId, selectedFieldId],
  );

  const ndviRasterCandidates = useMemo(
    () => effectiveRastersData
      ?.filter((raster) => isNdviRaster(raster))
      .sort(sortByLatestRaster),
    [effectiveRastersData, sortByLatestRaster],
  );
  const ndviBandPair = useMemo(() => {
    const cogBands = effectiveRastersData?.filter(isGeoTiffRaster) || [];
    const redBands = cogBands.filter(
      (raster) => String(raster?.method_name || "").trim().toLowerCase() === "red",
    );
    const nirBands = cogBands.filter(
      (raster) => String(raster?.method_name || "").trim().toLowerCase() === "nir",
    );

    const pairs = nirBands.flatMap((nir) =>
      redBands
        .filter((red) => String(red?.survey_id) === String(nir?.survey_id))
        .map((red) => ({ red, nir })),
    );

    return pairs.sort((a, b) => sortByLatestRaster(a.nir, b.nir))[0] || null;
  }, [effectiveRastersData, sortByLatestRaster]);
  const ortofotoRasterCandidates = useMemo(
    () => effectiveRastersData
      ?.filter((raster) => isPmtilesRaster(raster))
      .sort((a, b) => {
      const priorityDiff = getRasterPriority(b) - getRasterPriority(a);
      if (priorityDiff !== 0) return priorityDiff;
      return sortByLatestRaster(a, b);
      }),
    [effectiveRastersData, sortByLatestRaster],
  );
  const hasSelectedRasterLayer = layers.ortofoto || layers.ndvi;
  const selectedMapRaster = selectedFieldId
    ? layers.ndvi
      ? ndviRasterCandidates?.[0]
      : layers.ortofoto
        ? ortofotoRasterCandidates?.[0]
        : null
    : null;
  
  const selectedMapRasterId = selectedMapRaster?.id;
  const selectedMapRasterFile = selectedMapRaster?.raster_file;
  const selectedMapRasterKind = isGeoTiffRaster(selectedMapRaster)
    ? "geotiff"
    : "pmtiles";

  useEffect(() => {
    console.log("Layer selezionati:", layers);
    console.log("Candidati NDVI GeoTIFF:", ndviRasterCandidates);
    console.log("Candidati ortofoto PMTiles:", ortofotoRasterCandidates);
    console.log("Raster mappa rilevato:", selectedMapRaster);

    if (!selectedFieldId) {
      setDownloadedRaster(null);
      setRasterStatus("idle");
      return;
    }

    if (!hasSelectedRasterLayer) {
      setDownloadedRaster(null);
      setRasterStatus("idle");
      return;
    }

    if (rastersLoading) {
      setDownloadedRaster(null);
      setRasterStatus("loading");
      return;
    }

    if (!selectedMapRaster) {
      setDownloadedRaster(null);
      setRasterStatus("error");
      return;
    }

    let isMounted = true;
    let objectUrlToRevoke = null;

    const loadRasterInBackground = async () => {
      const loadDerivedNdvi = async () => {
        if (!layers.ndvi || !ndviBandPair) return false;

        const redUrl = createPublicRasterUrl(ndviBandPair.red?.raster_file);
        const nirUrl = createPublicRasterUrl(ndviBandPair.nir?.raster_file);
        if (!redUrl || !nirUrl) return false;

        console.info("NDVI GeoTIFF non disponibile: calcolo da bande Red/NIR", {
          redUrl,
          nirUrl,
          surveyId: ndviBandPair.nir?.survey_id,
        });

        const [redResponse, nirResponse] = await Promise.all([
          fetch(redUrl),
          fetch(nirUrl),
        ]);
        if (!redResponse.ok || !nirResponse.ok) return false;

        const [redArrayBuffer, nirArrayBuffer] = await Promise.all([
          redResponse.arrayBuffer(),
          nirResponse.arrayBuffer(),
        ]);
        if (!isMounted) return true;

        setDownloadedRaster({
          rasterKind: "ndvi-derived",
          redArrayBuffer,
          nirArrayBuffer,
          sourceKey: `ndvi-${ndviBandPair.nir?.survey_id}`,
        });
        setRasterStatus("applying");
        return true;
      };

      try {
        setRasterStatus("loading");
        setDownloadedRaster(null);
        // Prefer the public object for every supported raster format. GeoTIFF/COG
        // previously skipped S3 entirely, even when raster_file pointed to an
        // existing object, and went straight to the heavier API fallback.
        const publicRasterUrl = createPublicRasterUrl(selectedMapRasterFile);

        if (publicRasterUrl && await canReadPublicRasterUrl(publicRasterUrl)) {
          if (selectedMapRasterKind === "geotiff") {
            try {
              console.info("Download completo GeoTIFF/COG da S3:", publicRasterUrl);
              const s3Response = await fetch(publicRasterUrl);

              if (!s3Response.ok) {
                throw new Error(`Errore download S3 GeoTIFF: ${s3Response.status} ${s3Response.statusText}`);
              }

              const arrayBuffer = await s3Response.arrayBuffer();
              const blob = new Blob([arrayBuffer], {
                type: s3Response.headers.get("content-type") || "image/tiff",
              });
              const fileUrl = window.URL.createObjectURL(blob);
              objectUrlToRevoke = fileUrl;

              if (isMounted) {
                setDownloadedRaster({
                  blob,
                  url: fileUrl,
                  arrayBuffer,
                  rasterKind: selectedMapRasterKind,
                  sourceKey: publicRasterUrl,
                  shouldRevoke: true,
                });
                setRasterStatus("ready");
              }
              return;
            } catch (error) {
              console.error("Errore download completo GeoTIFF/COG da S3, fallback a /raster/{id}:", {
                url: publicRasterUrl,
                name: error?.name,
                message: error?.message,
                stack: error?.stack,
                error,
              });
            }
          } else {
            if (isMounted) {
              setDownloadedRaster({
                url: publicRasterUrl,
                rasterKind: selectedMapRasterKind,
                sourceKey: publicRasterUrl,
                shouldRevoke: false,
              });
              setRasterStatus("ready");
            }
            return;
          }
        }

        if (publicRasterUrl) {
          console.warn("URL raster pubblico non utilizzabile via browser, fallback a /raster/{id}", publicRasterUrl);
        }

        const inlineRaster = createPmtilesObjectUrlFromBase64(
          getRasterBase64Payload(selectedMapRasterFile),
        );

        if (inlineRaster) {
          objectUrlToRevoke = inlineRaster.shouldRevoke ? inlineRaster.url : null;
          if (isMounted) {
            setDownloadedRaster({
              ...inlineRaster,
              rasterKind: selectedMapRasterKind,
            });
            setRasterStatus("ready");
          }
          return;
        }

        if (!selectedMapRasterId) {
          setDownloadedRaster(null);
          setRasterStatus("error");
          return;
        }

        const urlDownload = buildDigitalTwinDataUrl(`raster/${selectedMapRasterId}`);
        console.log(`Avvio fetch raster completo per l'URL: ${urlDownload}`);

        const response = await fetch(urlDownload, {
          headers: getDigitalTwinDataHeaders(),
        });
        
        if (!isMounted) return;
        if (!response.ok) {
          let errorDetail = "";
          try {
            const errorPayload = await response.json();
            errorDetail = errorPayload?.detail ? ` - ${errorPayload.detail}` : "";
          } catch (parseError) {
            errorDetail = "";
          }
          throw new Error(`Errore download raster: ${response.status}${errorDetail}`);
        }

        const onProgress = (percentuale) => {
          if (isMounted) {
            console.log(`Download del raster ${selectedMapRaster.method_name} al ${percentuale}%`);
          }
        };

        const onFileReady = (fileData) => {
          if (isMounted) {
            console.log("File scaricato e pronto in background!", fileData);
            objectUrlToRevoke = fileData.url;
            setDownloadedRaster({
              ...fileData,
              rasterKind: selectedMapRasterKind,
              sourceKey: `raster-${selectedMapRasterId}.${selectedMapRasterKind === "geotiff" ? "tif" : "pmtiles"}`,
            });
            setRasterStatus("ready");
          }
        };

        const onError = (errorMessage) => {
          if (isMounted) {
            console.error("Errore handleDownload:", errorMessage);
            setDownloadedRaster(null);
            setRasterStatus("error");
          }
        };

        const contentType = response.headers.get("Content-Type") || "";

        if (contentType.includes("application/json")) {
          const jsonPayload = await response.json();
          const rasterData = createPmtilesObjectUrlFromBase64(
            getRasterBase64Payload(jsonPayload),
          );

          if (!rasterData) {
            console.warn("La risposta JSON del raster non contiene un PMTiles/base64/URL valido", jsonPayload);
            setDownloadedRaster(null);
            setRasterStatus("error");
            return;
          }

          objectUrlToRevoke = rasterData.shouldRevoke ? rasterData.url : null;
          if (isMounted) {
            setDownloadedRaster({
              ...rasterData,
              rasterKind: selectedMapRasterKind,
              sourceKey: `raster-${selectedMapRasterId}.${selectedMapRasterKind === "geotiff" ? "tif" : "pmtiles"}`,
            });
            setRasterStatus("applying");
          }
          return;
        }

        await handleDownload(response, onProgress, onFileReady, onError);

      } catch (error) {
        console.error("Errore durante il fetch del file:", error);
        if (isMounted) {
          try {
            if (await loadDerivedNdvi()) return;
          } catch (derivedError) {
            console.error("Errore durante il calcolo NDVI da Red/NIR:", derivedError);
          }
          if (isMounted) {
            setDownloadedRaster(null);
            setRasterStatus("error");
          }
        }
      }
    };

    loadRasterInBackground();

    return () => {
      isMounted = false;
      if (objectUrlToRevoke) {
        window.URL.revokeObjectURL(objectUrlToRevoke);
      }
    };
  }, [
    layers,
    hasSelectedRasterLayer,
    ndviRasterCandidates,
    ndviBandPair,
    ortofotoRasterCandidates,
    rastersLoading,
    selectedFieldId,
    selectedMapRaster,
    selectedMapRasterFile,
    selectedMapRasterId,
    selectedMapRasterKind,
  ]);

  if (!isLoaded) return <p>Loading map…</p>;

  return (
    <OpenStreetMap
      selectedField={selectedField}
      selectedFarm={selectedFarm}
      fieldsData={fieldsData ?? []}
      farmsData={farmsData ?? []}
      polygons={polygons ?? []}
      farmPolygons={farmPolygons ?? []}
      polylines={polylines ?? []}
      plantsData={plantsData ?? []}
      plantsMeasurementsData={plantsMeasurementsData ?? []}
      downloadedRaster={downloadedRaster}
      rasterStatus={rasterStatus}
      setRasterStatus={setRasterStatus}
      selectedRaster={selectedMapRaster}
      latestSurvey={latestSurvey}
      layers={layers}
      setLayers={setLayers}
      fieldImagesData={fieldImagesData}
      fieldImagesLoading={fieldImagesLoading}
      selectedFieldImage={selectedFieldImage}
      selectedFieldImageId={selectedFieldImageId}
      fieldImageDetailLoading={fieldImageDetailLoading}
    />
  );
}
